import { Box, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useGetReleaseTrackFileVersionURL } from "../../../../api/files/hooks/useGetReleaseTrackFileVersionURL";
import {
  failedReleaseTrackUploadStateAtom,
  uploadReleaseTrackIdAtom,
  uploadReleaseTrackSongTitleAtom,
  uploadReleaseTrackStateAtom,
} from "../../../../atoms/releases/uploadReleaseTrackAtom";
import { useGetFailedTrackFileStatus } from "../../../../atoms/supplychain/supplyChainFailedUploadsAtom";
import { QUERY_KEYS } from "../../../../constants/queryKeys";
import { FileType } from "../../../../store/models/fileVersion";
import { PlaylistTrack } from "../../../../store/models/playListTrack/types";
import { useHandleReleaseTrackFileUpload } from "../../../../utils/releaseTrackUtils";
import {
  Button,
  ButtonVariant,
} from "../../../core-ui/components/Button/Button";
import { Text } from "../../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../../core-ui/components/Text/TextUtils";
import { AlertCard } from "../../../screens/Error/components/AlertCard";
import { Uploader } from "../../Uploader/Uploader";
import { UploadReleaseTrackLoading } from "./components/UploadReleaseTrackLoading";
import { UploadReleaseTrackPlayer } from "./UploadReleaseTrackPlayer";
import { uploadSidePanelAtom } from "../../../../atoms/location/uploadSidePanelAtom";

interface UploadReleaseTrackFormProps {
  handleCancel: () => void;
  handleSave: () => void;
  releaseId: number;
  releaseTrackId: number | undefined;
  uploadedTrack?: PlaylistTrack;
}

export const UploadReleaseTrackForm = ({
  handleCancel,
  handleSave,
  releaseId,
  releaseTrackId,
  uploadedTrack,
}: UploadReleaseTrackFormProps) => {
  const { handleUpload, progressState, isPending, handleCancelUpload } =
    useHandleReleaseTrackFileUpload(releaseId);
  const queryClient = useQueryClient();
  const uploadReleaseTrackState = useAtomValue(uploadReleaseTrackStateAtom);
  const failedUpload = useGetFailedTrackFileStatus(
    releaseTrackId || uploadReleaseTrackState.failedReleaseTrackId || -1,
  );
  const isSidePanelOpen = useAtomValue(uploadSidePanelAtom);

  const { data: getReleaseTrackUrl } = useGetReleaseTrackFileVersionURL(
    uploadedTrack?.fileVersionId || undefined,
  );

  const isUploading = useMemo(
    () => isPending || (progressState > 0 && progressState < 100),
    [isPending, progressState],
  );

  const { songTitle } = useAtomValue(uploadReleaseTrackStateAtom);
  const setSongTitle = useSetAtom(uploadReleaseTrackSongTitleAtom);
  const setReleaseTrackId = useSetAtom(uploadReleaseTrackIdAtom);
  const setFailedReleaseTrack = useSetAtom(failedReleaseTrackUploadStateAtom);

  const showUploadStatus = useMemo(() => {
    return isUploading || Boolean(!isUploading && !uploadedTrack && songTitle);
  }, [isUploading, uploadedTrack, songTitle]);

  useEffect(() => {
    if (!isSidePanelOpen && isPending) {
      handleCancelUpload();
    }
  }, [isPending, isSidePanelOpen, handleCancelUpload]);

  return (
    <Stack gap={3}>
      <Text component="h1" variant={TextStyleVariant.H4}>
        Upload Track
      </Text>
      {failedUpload && (
        <AlertCard
          message={`Error: ${failedUpload.errorMessage}`}
          margin={0}
          fullWidth
        />
      )}
      <Box position="relative">
        {!uploadedTrack && !isUploading && !songTitle && (
          <Uploader
            headerText="Drag & drop a file here, or click to browse"
            instructionText="Accepted file types: .wav"
            acceptedFiles={[FileType.WAV]}
            uploaderDisabled={isUploading}
            onDrop={async (acceptedFiles) => {
              const uploadedFile = acceptedFiles[0];
              setSongTitle(uploadedFile.name);
              const { releaseTrackData } = await handleUpload(acceptedFiles);
              if (releaseTrackData?.id) {
                setReleaseTrackId(releaseTrackData.id);
              }
            }}
          />
        )}
        {showUploadStatus && (
          <UploadReleaseTrackLoading
            handleCancelUpload={() => {
              handleCancelUpload();
              setFailedReleaseTrack();
              void queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.FETCH_ARTIST_RELEASE_TRACKS, releaseId],
              });
            }}
            progressState={progressState}
            songTitle={songTitle}
            isFileValidating={Boolean(
              !isUploading && !uploadedTrack && songTitle,
            )}
          />
        )}
        {!isUploading && uploadedTrack && getReleaseTrackUrl?.url && (
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ width: "100%" }}>
              <UploadReleaseTrackPlayer
                uploadedTrack={uploadedTrack}
                releaseId={releaseId}
                fileName={songTitle}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Stack direction="row" justifyContent="center" gap={2}>
        <Button
          fullWidth
          variant={ButtonVariant.BLACK_OUTLINED_BUTTON_BLACK_HOVER}
          onClick={() => handleCancel()}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          disabled={!uploadedTrack}
          onClick={() => handleSave()}
        >
          Save Song
        </Button>
      </Stack>
    </Stack>
  );
};
